import React from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import { Icon } from '@iconify/react'
import Switch from '@mui/material/Switch'
import Tooltip from '@mui/material/Tooltip'
import { FEATURE_KEYS } from '@/src/constants/featureExperience'
import { useFeatureVersion } from '@/src/hooks/useFeatureVersion'
import { useToggleFeature } from '@/src/hooks/useToggleFeature'
import type { FeatureVersion } from '@/src/types/featureExperience'
import { FeatureToggle } from '@/src/components/featureExperience/FeatureToggle'
import {
  SIDEBAR_NAVIGATION_PILOT_BODY,
  SIDEBAR_NAVIGATION_PILOT_SHORT
} from '@/src/components/featureExperience/sidebarNavigationPilotCopy'
import {
  sidebarNavigationCollapsedSwitchTooltip,
  sidebarNavigationResolvedDescription
} from '@/src/components/featureExperience/sidebarNavigationMessaging'

/** LEGACY_MATERIAL_NAV — remove toggle (and rail/sidebar slots) after Bhyve-only; see legacyMaterialNavCutover. */
const FEATURE_KEY = FEATURE_KEYS.SIDEBAR_NAVIGATION

/**
 * Layout chrome (padding, border-top) should only wrap the sidebar toggle when something renders.
 * Expanded matches {@link FeatureToggle}; collapsed hides when forced/no toggle (pilot strip is separate).
 */
export function useSidebarNavExperienceToggleSlotVisible(collapsed: boolean): boolean {
  const { canToggle, cutoverReached, isInRolloutAudience, isForcedResolution, phase } = useFeatureVersion(FEATURE_KEY)

  if (!isInRolloutAudience || cutoverReached) {
    return false
  }

  if (!collapsed) {
    return true
  }

  return !isForcedResolution && (phase === 'pilot' || canToggle)
}

function sidebarLabel(version: FeatureVersion): string {
  return version === 'next' ? 'Navigation: New' : 'Navigation: Classic'
}

/** Collapsed rail: compact switch only (hooks live here — not conditional with expanded). */
const SidebarNavExperienceToggleCollapsed: React.FC = () => {
  const { canToggle, lockAfterOptInToNext, cutoverReached, isInRolloutAudience, isForcedResolution, phase } =
    useFeatureVersion(FEATURE_KEY)
  const { currentVersion, toggle, isLoading } = useToggleFeature(FEATURE_KEY)

  if (phase === 'pilot' && isInRolloutAudience && !cutoverReached && !isForcedResolution) {
    return (
      <Tooltip title={SIDEBAR_NAVIGATION_PILOT_BODY}>
        <Alert
          severity='info'
          variant='standard'
          icon={<Icon icon='mdi:information' width={18} height={18} />}
          sx={{
            py: 0.75,
            px: 1,
            maxWidth: 200,
            bgcolor: 'transparent',
            backgroundImage: 'none',
            boxShadow: 'none',
            color: theme => theme.palette.info.main,
            '& .MuiAlert-message': {
              padding: '2px 0',
              fontSize: '0.6875rem',
              lineHeight: 1.35,
              overflow: 'hidden'
            },
            '& .MuiAlert-icon': {
              alignSelf: 'flex-start',
              mr: 0.75,
              color: theme => theme.palette.info.main,
              opacity: 1,
              display: 'flex'
            }
          }}
        >
          {SIDEBAR_NAVIGATION_PILOT_SHORT}
        </Alert>
      </Tooltip>
    )
  }

  if (!isInRolloutAudience || !canToggle || isForcedResolution || cutoverReached) {
    return null
  }

  if (lockAfterOptInToNext && currentVersion === 'next') {
    return (
      <Tooltip title={`${sidebarLabel('next')} (locked)`}>
        <span>
          <Switch checked disabled size='small' sx={{ '& .MuiSwitch-track': { opacity: 0.5 } }} />
        </span>
      </Tooltip>
    )
  }

  const tip = sidebarNavigationCollapsedSwitchTooltip(currentVersion, phase)

  return (
    <Tooltip title={tip}>
      <span>
        <Switch
          checked={currentVersion === 'next'}
          onChange={() => void toggle()}
          disabled={isLoading}
          size='small'
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD204' },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFD204' }
          }}
          inputProps={{ 'aria-label': tip }}
        />
      </span>
    </Tooltip>
  )
}

interface SidebarNavExperienceToggleProps {
  collapsed?: boolean

  /** Light BHYBE rail: readable text on gray (default is dark classic sidebar). */
  surface?: 'dark' | 'light'
}

interface SidebarNavExperienceToggleExpandedProps {
  surface: 'dark' | 'light'
}

const SidebarNavExperienceToggleExpanded: React.FC<SidebarNavExperienceToggleExpandedProps> = ({ surface }) => {
  const { phase } = useFeatureVersion(FEATURE_KEY)

  const lightSx =
    surface === 'light'
      ? {
          width: '100%',
          '& .MuiFormControlLabel-root': { alignItems: 'flex-start', marginLeft: 0, marginRight: 0 },
          '& .MuiFormControlLabel-label': { width: '100%', paddingTop: '2px' },
          '& .MuiTypography-subtitle2': { color: '#171717 !important' },
          '& .MuiTypography-caption': { color: 'rgba(23, 23, 23, 0.62) !important' },
          '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD204' },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFD204 !important' }
        }
      : {
          width: '100%',
          '& .MuiFormControlLabel-root': { alignItems: 'flex-start', marginLeft: 0, marginRight: 0 },
          '& .MuiFormControlLabel-label': { width: '100%' },
          '& .MuiTypography-root': { color: '#fff' },
          '& .MuiTypography-caption': { color: 'rgba(255,255,255,0.75) !important' }
        }

  const resolveDescription = (version: FeatureVersion) => sidebarNavigationResolvedDescription(version, phase)

  return (
    <Box sx={lightSx} onClick={e => e.stopPropagation()}>
      <FeatureToggle
        featureKey={FEATURE_KEY}
        label='New navigation'
        description='Try the updated sidebar layout.'
        resolveLabel={sidebarLabel}
        resolveDescription={resolveDescription}
      />
    </Box>
  )
}

/**
 * Bottom-of-sidebar control to opt in/out of the new navigation (feature experience).
 */
export const SidebarNavExperienceToggle: React.FC<SidebarNavExperienceToggleProps> = ({
  collapsed,
  surface = 'dark'
}) => {
  if (collapsed) {
    return <SidebarNavExperienceToggleCollapsed />
  }

  return <SidebarNavExperienceToggleExpanded surface={surface} />
}
